import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { MODULES, RESOURCES, RECORDINGS_FOLDER, CLAUDE_PROJECT_LINK } from '../constants'
import './ModuleDetail.css'

export default function ModuleDetail() {
  const navigate = useNavigate()
  const { moduleNum } = useParams()
  const idx = parseInt(moduleNum) - 1
  const mod = MODULES[idx]

  const [user, setUser] = useState(null)
  const [recording, setRecording] = useState(null)
  const [profile, setProfile] = useState(null)
  const [progress, setProgress] = useState(null)
  const [gmNote, setGmNote] = useState('')
  const [adminNote, setAdminNote] = useState('')
  const [prework, setPrework] = useState([])
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [marking, setMarking] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (mod) setPrework(mod.prework.map(() => false))
  }, [mod])

  useEffect(() => {
    async function load() {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) { navigate('/login'); return }
      setUser(session.user)
      const { data: profileData } = await supabase.from('profiles').select('*').eq('id', session.user.id).single()
      setProfile(profileData)
      const { data: progressData } = await supabase.from('module_progress').select('*').eq('user_id', session.user.id).eq('curriculum', 'branding').eq('module_number', idx + 1).single()
      const { data: recData } = await supabase.from('recordings').select('*').eq('curriculum', 'branding').eq('module_number', idx + 1).eq('published', true).single()
      if (recData) setRecording(recData)
      if (progressData) {
        setProgress(progressData)
        setGmNote(progressData.gm_note || '')
        setAdminNote(progressData.admin_note || '')
      }
      setLoading(false)
    }
    load()
  }, [navigate, idx])

  const handleMarkComplete = async () => {
    setMarking(true)
    const { data } = await supabase.from('module_progress').upsert({
      user_id: user.id, curriculum: 'branding', module_number: idx + 1,
      completed_at: new Date().toISOString(), gm_note: gmNote, admin_note: adminNote,
    }, { onConflict: 'user_id,curriculum,module_number' }).select().single()
    if (data) setProgress(data)
    setMarking(false)
  }

  const handleSaveNote = async () => {
    setSaving(true)
    await supabase.from('module_progress').upsert({
      user_id: user.id, curriculum: 'branding', module_number: idx + 1,
      gm_note: gmNote, completed_at: progress?.completed_at || null,
    }, { onConflict: 'user_id,curriculum,module_number' })
    setSaving(false); setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const togglePrework = (i) => {
    setPrework(prev => prev.map((v, j) => j === i ? !v : v))
  }

  if (!mod) return <div style={{padding:'40px',textAlign:'center',color:'#888'}}>Module not found.</div>
  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#0D3B66', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: '#A7D8F5', fontFamily: 'Georgia, serif', fontSize: '16px', fontStyle: 'italic' }}>Loading...</p>
    </div>
  )

  const isComplete = !!progress?.completed_at
  const moduleResources = (mod.resources || []).map(id => RESOURCES[id]).filter(Boolean)

  return (
    <div className="mod">
      <nav className="mod-nav">
        <span className="mod-nav-logo">VACATIA UNIVERSITY</span>
        <button className="mod-nav-back" onClick={() => navigate('/dashboard')}>← Back to dashboard</button>
      </nav>

      <header className="mod-header">
        <div className="mod-header-inner">
          <div className="mod-num-badge">Module {mod.number}</div>
          <h1 className="mod-title">{mod.title}</h1>
          <p className="mod-sub">Curriculum 01: Branding &nbsp;·&nbsp; {profile?.property}</p>
        </div>
        {isComplete && <div className="mod-complete-badge">✓ Attended</div>}
      </header>

      <main className="mod-main">

        {/* About */}
        <section className="mod-section">
          <p className="mod-label">About this session</p>
          <p className="mod-body">{mod.description}</p>
        </section>

        {/* Pre-work */}
        <section className="mod-section">
          <p className="mod-label">Before the session</p>
          <p className="mod-body" style={{marginBottom:'16px'}}>Complete these before joining the live call. Check each one off as you go.</p>
          <div className="mod-prework">
            {mod.prework.map((item, i) => (
              <div key={i} className={`mod-prework-item ${prework[i] ? 'done' : ''}`} onClick={() => togglePrework(i)}>
                <span className="mod-check">{prework[i] ? '✓' : '○'}</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Session recording */}
        <section className="mod-section">
          <p className="mod-label">Session recording</p>
          {recording ? (
            <div className="mod-recording">
              <div className="mod-recording-card">
                <div className="mod-recording-icon">🎬</div>
                <div className="mod-recording-info">
                  <p className="mod-recording-title">{recording.session_title || mod.title}</p>
                  {recording.session_date && (
                    <p className="mod-recording-date">Recorded {new Date(recording.session_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                  )}
                  <p className="mod-recording-note">Opens in Google Drive. No download required - plays in your browser.</p>
                </div>
                <a href={recording.recording_url} target="_blank" rel="noreferrer" className="mod-recording-btn">Watch recording →</a>
              </div>
            </div>
          ) : (
            <div className="mod-recording-pending">
              <p className="mod-body">{isComplete ? 'The recording is being processed and will appear here shortly.' : 'The recording will be available here after the live session.'}</p>
            </div>
          )}
        </section>

        {/* Module-specific resources */}
        {moduleResources.length > 0 && (
          <section className="mod-section">
            <p className="mod-label">Resources for this module</p>
            <div className="mod-resources">
              {moduleResources.map(res => (
                <a key={res.id} href={res.link} target="_blank" rel="noreferrer" className="mod-resource-card">
                  <span className="mod-resource-icon">{res.icon}</span>
                  <div>
                    <p className="mod-resource-title">{res.title}</p>
                    <p className="mod-resource-desc">{res.description}</p>
                  </div>
                  <span className="mod-resource-arrow">→</span>
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Join session */}
        <section className="mod-section">
          <p className="mod-label">Join the live session</p>
          <div className="mod-meet">
            <div className="mod-meet-info">
              <p className="mod-meet-platform">Google Meet</p>
              <p className="mod-meet-note">90 minutes. All GMs on together. Recorded and available here after the call.</p>
            </div>
            <a href="https://meet.google.com" target="_blank" rel="noreferrer" className="mod-meet-btn">Join Google Meet</a>
          </div>
        </section>

        {/* Mark attended */}
        {!isComplete && (
          <section className="mod-section">
            <p className="mod-label">Attendance</p>
            <p className="mod-body" style={{marginBottom:'16px'}}>After attending the live session, mark it here. This updates your progress on the dashboard.</p>
            <button className="mod-complete-btn" onClick={handleMarkComplete} disabled={marking}>
              {marking ? 'Saving...' : 'Mark session as attended'}
            </button>
          </section>
        )}

        {/* GM note */}
        <section className="mod-section">
          <p className="mod-label">Leave a note for Sam</p>
          <p className="mod-body" style={{marginBottom:'12px'}}>A question, a win, a challenge, or anything you want to share after this session. Sam will write back here.</p>
          <textarea className="mod-textarea" placeholder="What came up for you in this session? What do you need?" value={gmNote} onChange={e => setGmNote(e.target.value)} rows={4} />
          <button className="mod-save-btn" onClick={handleSaveNote} disabled={saving}>
            {saved ? 'Saved.' : saving ? 'Saving...' : 'Save note'}
          </button>
        </section>

        {/* Admin note back */}
        {adminNote && (
          <section className="mod-section">
            <p className="mod-label">Note from Sam</p>
            <div className="mod-admin-note"><p>{adminNote}</p></div>
          </section>
        )}

        {/* Voice tool CTA */}
        <a className="mod-voice-cta" href={CLAUDE_PROJECT_LINK} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
          <span className="mod-voice-icon">💬</span>
          <div>
            <p className="mod-voice-title">Need to communicate something from this session?</p>
            <p className="mod-voice-desc">Use the Vacatia Claude Project to get an on-brand response for any situation.</p>
          </div>
          <span className="mod-voice-arrow">→</span>
        </a>

      </main>

      <footer className="mod-footer">
        <p>Vacatia University &nbsp;·&nbsp; Est. 2026 &nbsp;·&nbsp; <em>Leave better than you arrived.</em></p>
      </footer>
    </div>
  )
}
